import { all, call, put, takeLatest } from "redux-saga/effects";
import queryApi from "../../utils/fetchApi";
import Urler from "../../utils/serviceUrl";
import { updateUserLocation } from "../user/user.actions";
import {
  createStripeConnectFailure,
  createStripeConnectSuccess,
  fetchBalanceFailure,
  fetchBalanceStart,
  fetchBalanceSuccess,
  fetchCardsFailure,
  fetchCardsStart,
  fetchCardsSuccess,
  fetchCountriesFailure,
  fetchCountriesSuccess,
  fetchDepositsFailure,
  fetchDepositsSuccess,
  fetchRatesFailure,
  fetchRatesSuccess,
  fetchStripeConnectStatusFailure,
  fetchStripeConnectStatusSuccess,
  fetchWalletHistoryFailure,
  fetchWalletHistorySuccess,
  initDepositFailure,
  initDepositSuccess,
  requestWithdrawalFailure,
  requestWithdrawalSuccess,
  updateLocationFailure,
  updateLocationSuccess,
  verifyPaystackPaymentFailure,
  verifyPaystackPaymentSuccess,
} from "./wallet.actions";
import WalletActionTypes from "./wallet.types";

// Fetch supported countries
export function* fetchCountries() {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler("countries"),
    });
    if (res.status === "Success") {
      yield put(fetchCountriesSuccess(res.data.countries));
    } else {
      yield put(
        fetchCountriesFailure(res.message || "Failed to fetch countries"),
      );
    }
  } catch (e) {
    yield put(fetchCountriesFailure(e.message));
  }
}

// Verify Paystack payment
export function* verifyPaystackPayment({
  payload: { token, reference, depositId },
}) {
  try {
    const res = yield queryApi({
      method: "POST",
      url: Urler("deposits/verify-paystack"),
      bearer: token,
      data: { reference, depositId },
    });

    if (res.status === "Success") {
      yield put(verifyPaystackPaymentSuccess(res));
      yield put(fetchBalanceStart(token));
      yield put(fetchCardsStart(token));
    } else {
      yield put(
        verifyPaystackPaymentFailure(
          res.message || "Failed to verify Paystack payment",
        ),
      );
    }
  } catch (e) {
    yield put(verifyPaystackPaymentFailure(e.message));
  }
}

// Update user location
export function* updateLocation({ payload: { token, country, location } }) {
  try {
    const res = yield queryApi({
      method: "PATCH",
      url: Urler("users/updateLocation"),
      bearer: token,
      data: { country, location },
    });
    if (res.status === "Success") {
      // Update user's location in the user reducer
      yield put(
        updateUserLocation({
          country: res.data.user.country,
          preferredCurrency: res.data.user.preferredCurrency,
        }),
      );
      yield put(updateLocationSuccess(res.data));
    } else {
      yield put(
        updateLocationFailure(res.message || "Failed to update location"),
      );
    }
  } catch (e) {
    yield put(updateLocationFailure(e.message));
  }
}

// Fetch exchange rates
export function* fetchRates() {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler("deposits/rates"),
    });
    if (res.status === "Success") {
      yield put(fetchRatesSuccess(res.data.rates));
    } else {
      yield put(fetchRatesFailure(res.message || "Failed to fetch rates"));
    }
  } catch (e) {
    yield put(fetchRatesFailure(e.message));
  }
}

// Initialize deposit
export function* initDeposit({ payload: { token, amount, currency, cardId } }) {
  try {
    const data = { amount, currency };
    if (cardId) {
      data.cardId = cardId;
    }
    const res = yield queryApi({
      method: "POST",
      url: Urler("deposits"),
      bearer: token,
      data,
    });
    if (res.status === "Success") {
      yield put(initDepositSuccess(res));
      // If saved card payment succeeded immediately, refresh balance and cards
      if (res.paymentStatus === "succeeded") {
        yield put(fetchBalanceStart(token));
        yield put(fetchCardsStart(token));
      }
    } else {
      yield put(
        initDepositFailure(res.message || "Failed to initialize deposit"),
      );
    }
  } catch (e) {
    yield put(initDepositFailure(e.message));
  }
}

// Fetch deposit history
export function* fetchDeposits({ payload: { token } }) {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler("deposits"),
      bearer: token,
    });
    if (res.status === "Success") {
      yield put(fetchDepositsSuccess(res.data.deposits));
    } else {
      yield put(
        fetchDepositsFailure(res.message || "Failed to fetch deposits"),
      );
    }
  } catch (e) {
    yield put(fetchDepositsFailure(e.message));
  }
}

// Request withdrawal
export function* requestWithdrawal({
  payload: {
    token,
    amount,
    coins,
    accountNumber,
    bankCode,
    accountName,
    bankName,
  },
}) {
  try {
    const res = yield queryApi({
      method: "POST",
      url: Urler("withdrawals/request"),
      bearer: token,
      data: { amount, coins, accountNumber, bankCode, accountName, bankName },
    });
    if (res.status === "success") {
      yield put(requestWithdrawalSuccess(res.data));
    } else {
      yield put(
        requestWithdrawalFailure(res.message || "Failed to request withdrawal"),
      );
    }
  } catch (e) {
    yield put(requestWithdrawalFailure(e.message));
  }
}

// Watchers
export function* onFetchCountriesStart() {
  yield takeLatest(WalletActionTypes.FETCH_COUNTRIES_START, fetchCountries);
}

export function* onUpdateLocationStart() {
  yield takeLatest(WalletActionTypes.UPDATE_LOCATION_START, updateLocation);
}

export function* onFetchRatesStart() {
  yield takeLatest(WalletActionTypes.FETCH_RATES_START, fetchRates);
}

export function* onInitDepositStart() {
  yield takeLatest(WalletActionTypes.INIT_DEPOSIT_START, initDeposit);
}

export function* onVerifyPaystackPaymentStart() {
  yield takeLatest(
    WalletActionTypes.VERIFY_PAYSTACK_PAYMENT_START,
    verifyPaystackPayment,
  );
}

export function* onFetchDepositsStart() {
  yield takeLatest(WalletActionTypes.FETCH_DEPOSITS_START, fetchDeposits);
}

export function* onRequestWithdrawalStart() {
  yield takeLatest(
    WalletActionTypes.REQUEST_WITHDRAWAL_START,
    requestWithdrawal,
  );
}

// Fetch saved cards (all gateways)
export function* fetchCards({ payload: { token } }) {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler("cards/all"),
      bearer: token,
    });
    if (res.status === "Success") {
      yield put(fetchCardsSuccess(res.data.cards));
    } else {
      yield put(fetchCardsFailure(res.message || "Failed to fetch cards"));
    }
  } catch (e) {
    yield put(fetchCardsFailure(e.message));
  }
}

export function* onFetchCardsStart() {
  yield takeLatest(WalletActionTypes.FETCH_CARDS_START, fetchCards);
}

// Fetch balance
export function* fetchBalance({ payload: { token } }) {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler("coins/my-balance"),
      bearer: token,
    });
    if (res.status === "Success") {
      yield put(fetchBalanceSuccess(res.data.balance));
    } else {
      yield put(fetchBalanceFailure(res.message || "Failed to fetch balance"));
    }
  } catch (e) {
    yield put(fetchBalanceFailure(e.message));
  }
}

export function* onFetchBalanceStart() {
  yield takeLatest(WalletActionTypes.FETCH_BALANCE_START, fetchBalance);
}

// Fetch wallet history (merged deposits & withdrawals)
export function* fetchWalletHistory({ payload: { token } }) {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler("transactions/wallet-history"),
      bearer: token,
    });
    if (res.status === "success") {
      yield put(fetchWalletHistorySuccess(res.data.history));
    } else {
      yield put(
        fetchWalletHistoryFailure(res.message || "Failed to fetch history"),
      );
    }
  } catch (e) {
    yield put(fetchWalletHistoryFailure(e.message));
  }
}

export function* onFetchWalletHistoryStart() {
  yield takeLatest(
    WalletActionTypes.FETCH_WALLET_HISTORY_START,
    fetchWalletHistory,
  );
}

// Create Stripe Connect account
export function* createStripeConnect({ payload: { token } }) {
  try {
    const res = yield queryApi({
      method: "POST",
      url: Urler("withdrawals/stripe-connect/create"),
      bearer: token,
    });
    if (res.status === "success") {
      yield put(createStripeConnectSuccess(res.data));
      // Open onboarding URL in new tab if provided
      if (res.data.onboardingUrl) {
        window.open(res.data.onboardingUrl, "_blank");
      }
    } else {
      yield put(
        createStripeConnectFailure(
          res.message || "Failed to create Stripe Connect account",
        ),
      );
    }
  } catch (e) {
    yield put(createStripeConnectFailure(e.message));
  }
}

export function* onCreateStripeConnectStart() {
  yield takeLatest(
    WalletActionTypes.CREATE_STRIPE_CONNECT_START,
    createStripeConnect,
  );
}

// Fetch Stripe Connect status
export function* fetchStripeConnectStatus({ payload: { token } }) {
  try {
    const res = yield queryApi({
      method: "GET",
      url: Urler("withdrawals/stripe-connect/status"),
      bearer: token,
    });
    if (res.status === "success") {
      yield put(fetchStripeConnectStatusSuccess(res.data));
    } else {
      yield put(
        fetchStripeConnectStatusFailure(
          res.message || "Failed to fetch Stripe Connect status",
        ),
      );
    }
  } catch (e) {
    yield put(fetchStripeConnectStatusFailure(e.message));
  }
}

export function* onFetchStripeConnectStatusStart() {
  yield takeLatest(
    WalletActionTypes.FETCH_STRIPE_CONNECT_STATUS_START,
    fetchStripeConnectStatus,
  );
}

export function* walletSagas() {
  yield all([
    call(onFetchCountriesStart),
    call(onUpdateLocationStart),
    call(onFetchRatesStart),
    call(onInitDepositStart),
    call(onVerifyPaystackPaymentStart),
    call(onFetchDepositsStart),
    call(onRequestWithdrawalStart),
    call(onFetchCardsStart),
    call(onFetchBalanceStart),
    call(onFetchWalletHistoryStart),
    call(onCreateStripeConnectStart),
    call(onFetchStripeConnectStatusStart),
  ]);
}
